import { Connection, PublicKey } from "@solana/web3.js";
import { backendApi } from "src/assets/constants";
import { Buffer } from "buffer";
import { AccountLayout, u64, MintInfo, MintLayout } from "@solana/spl-token";

export const getMintInfo = async ({ mintAddress }) => {
  const connection = new Connection(backendApi, "confirmed");
  if (!connection) {
    return undefined;
  }
  const mint = new PublicKey(mintAddress);

  const info = await connection.getAccountInfo(mint);

  if (info === null) {
    throw new Error("Failed to find mint account");
  }

  const mintData = deserializeMint(Buffer.from(info.data));

  return mintData;
};

export const getTokenBalance = async ({ solAddress, mintAddress }) => {
  const connection = new Connection(backendApi, "confirmed");
  if (!connection || !solAddress) {
    return undefined;
  }
  const ownerAddress = new PublicKey(solAddress);
  const mint = new PublicKey(mintAddress);

  const mintData = await getMintInfo({ mintAddress });

  return await connection
    .getTokenAccountsByOwner(ownerAddress, {
      mint,
    })
    .then((response) => {
      const data = deserializeAccount(response.value[0].account.data);

      const amount = data.amount.toNumber() / Math.pow(10, mintData.decimals);

      return amount;
    })
    .catch((error) => {
      console.log({ error });
      return undefined;
    });
};

const deserializeMint = (data: Buffer) => {
  if (data.length !== MintLayout.span) {
    throw new Error("Not a valid Mint");
  }

  const mintInfo = MintLayout.decode(data);

  if (mintInfo.mintAuthorityOption === 0) {
    mintInfo.mintAuthority = null;
  } else {
    mintInfo.mintAuthority = new PublicKey(mintInfo.mintAuthority);
  }

  mintInfo.supply = u64.fromBuffer(mintInfo.supply);
  mintInfo.isInitialized = mintInfo.isInitialized !== 0;

  if (mintInfo.freezeAuthorityOption === 0) {
    mintInfo.freezeAuthority = null;
  } else {
    mintInfo.freezeAuthority = new PublicKey(mintInfo.freezeAuthority);
  }

  return mintInfo;
};

const deserializeAccount = (data: Buffer) => {
  const accountInfo = AccountLayout.decode(data);
  accountInfo.mint = new PublicKey(accountInfo.mint);
  accountInfo.owner = new PublicKey(accountInfo.owner);
  accountInfo.amount = u64.fromBuffer(accountInfo.amount);

  if (accountInfo.delegateOption === 0) {
    accountInfo.delegate = null;
    accountInfo.delegatedAmount = new u64(0);
  } else {
    accountInfo.delegate = new PublicKey(accountInfo.delegate);
    accountInfo.delegatedAmount = u64.fromBuffer(accountInfo.delegatedAmount);
  }

  accountInfo.isInitialized = accountInfo.state !== 0;
  accountInfo.isFrozen = accountInfo.state === 2;

  if (accountInfo.isNativeOption === 1) {
    accountInfo.rentExemptReserve = u64.fromBuffer(accountInfo.isNative);
    accountInfo.isNative = true;
  } else {
    accountInfo.rentExemptReserve = null;
    accountInfo.isNative = false;
  }

  if (accountInfo.closeAuthorityOption === 0) {
    accountInfo.closeAuthority = null;
  } else {
    accountInfo.closeAuthority = new PublicKey(accountInfo.closeAuthority);
  }

  return accountInfo;
};
